// =============================================================================
// ui/tabs/Accounts.tsx — the tab where D47 stops being a type and becomes UI.
// -----------------------------------------------------------------------------
// This is the screen a trader reads MID-SESSION to decide whether there is room
// for another trade, and almost every number on it — balance, peak, drawdown
// floor, drawdown buffer, profit-target progress, consistency, days traded — is
// a function of the WHOLE trade history. Under the 45-day boot window they come
// out **wrong in the optimistic direction**: a bigger balance and more drawdown
// room than the trader actually has, self-consistent while wrong.
//
// `buildAccountCardModel` takes a `WholeBook`, not a `Population`, so the
// compiler refuses to compute them from a bounded read.
//
// ⚠ THE TEMPTING SHORTCUT is to show the 45-day number with an asterisk. That
// is worse than showing nothing: a drawdown buffer that is too generous is
// precisely the number that gets an evaluation blown. A blank with a reason
// cannot be misread; a wrong number with a footnote can.
//
// ── ⚠ SO THE TAB ASKS FOR THE WHOLE BOOK ITSELF, ON ARRIVAL ──
//
// The previous version put a "Load full history" button above an empty list.
// That is the same refusal with worse manners: pressing a button buys no safety,
// it only makes the screen the trader navigated to arrive empty. The read starts
// on mount and the numbers appear when they are real.
//
// ── WHAT IS ON THIS SCREEN, AND WHAT IS NOT ──
//
// Everything the old client's Accounts tab READS is here: the house-health
// strip, the filter and sort chips, four gauges per card, the lock and drawdown
// banners, Today's Loss Room, the consistency bar and the callout that states
// both ways out of a breach, Risk of Ruin, the missing-fields nudge, the final
// P&L on a closed account, and the collapsed Archived group.
//
// Of what it WRITES, two are here: creating an account and editing one. Archive,
// delete, the merge and dedupe reviews and Coach Me are not — and they are named
// on the tab's own card rather than left as buttons that do nothing, because a
// control that looks live and is not is the mistake this port has made twice.
// =============================================================================

import {
  useCallback, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { useAccounts } from '../state/accounts';
import { useProAction } from '../state/useProAction';
import { ProActionLimit } from '../shell/ProActionLimit';
import { PRO_ACTION_LABEL, PRO_ACTION_PITCH } from '../../selectors/proAction';
import { useJournal } from '../state/journal';
import { usePreferences } from '../state/preferences';
import { money, moneyPlain, num } from '../format';
import { isWholeBook, type MetricsRow, type Population } from '../../selectors/population';
import {
  buildAccountCardModel, buildHouseAggregate, barTone, cardFilterCounts,
  cardMatchesFilter, lifecycleLabel, sortAccountCards,
  type AccountCardModel, type CardAccount, type CardFilter, type CardSort,
  type GaugeSlot, type HouseAggregate,
} from '../../selectors/accountCards';
import type { Account } from '../../selectors/accounts';
import type { DayBoundaryMode } from '../../selectors/population';
import {
  USER_TEMPLATE_PREFIX, accountHasSaveableRules, buildPropTemplate,
  parsePropTemplates, suggestTemplateLabel,
} from '../../selectors/accountSetup';
import { AccountEditor } from './AccountEditor';
import { AccountSetupModal } from './AccountSetupModal';
import { SaveTemplateDialog } from './SaveTemplateDialog';
import { setAccountArchived } from '../../io/sync/archiveAccount';
import { countDeleteImpact, deleteAccount, type DeleteImpact } from '../../io/sync/deleteAccount';
import { newAccountId } from '../../io/sync/createAccount';
import { savePropTemplate } from '../../io/sync/propTemplates';
import { DeleteAccountDialog } from './DeleteAccountDialog';
import { RiskOfRuinModal } from './RiskOfRuinModal';
import { CompareAccountsModal } from './CompareAccountsModal';
import { usePatterns } from '../state/patterns';
import { loadComparisonWeek } from '../../io/sync/comparison';
import {
  COMPARISON_MIN_ACCOUNTS, buildComparisonBrief,
} from '../../selectors/accountComparison';
import { useSupabase } from '../state/supabase';
import { reportFailure } from '../../io/diagnostics/errorLog';
import { useMultiSource } from '../state/useMultiSource';
import { useDuplicateScan } from '../state/useDuplicateScan';
import { DedupeTradesModal } from './DedupeTradesModal';
import { MergeAccountsModal } from './MergeAccountsModal';
import { detectDuplicateAccounts } from '../../selectors/accountMerge';
import { dismissSetupNudge } from '../../io/sync/dismissSetupNudge';

const FILTERS: readonly { key: CardFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'eval', label: 'Eval' },
  { key: 'funded', label: 'Funded' },
  { key: 'locked', label: 'Locked' },
];

const SORTS: readonly { key: CardSort; label: string }[] = [
  { key: 'risk', label: 'At Risk First' },
  { key: 'pnl', label: 'P&L' },
  { key: 'status', label: 'Status' },
  { key: 'firm', label: 'Firm' },
];

/** Seven days back, as an ISO date. The week cell on the banner. */
function weekStartOf(today: string): string {
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 7);
  return d.toISOString().slice(0, 10);
}

/**
 * @param go — where a refused Pro action sends the reader (#76).
 */
/** What this tab should do the moment it mounts. Mirrors `TradesIntent`. */
export type AccountsIntent = 'new';

export function AccountsTab(
  { go, intent, onIntentHandled }: {
    go?: (tab: string) => void;
    /** Set by the Dashboard's "+ Create your first account". */
    intent?: AccountsIntent | null | undefined;
    onIntentHandled?: (() => void) | undefined;
  } = {},
) {
  const { phase, accounts, error, reload } = useAccounts();

  /**
   * ⚠ CLEARED ON ARRIVAL, not on close — the same rule `tradesIntent` follows
   * and for the same reason. Left set, walking Accounts → Dashboard → Accounts
   * by hand would remount this tab and pop a blank account form the reader never
   * asked for, on a tab they were only passing through.
   *
   * ⚠ The effect sits ABOVE the early returns because hooks must, and it fires
   * while `phase === 'loading'` — which is harmless: `autoOpen` is read by
   * `useState` when the empty state finally renders, and this only clears the
   * flag owned one level up.
   */
  const armed = intent === 'new';
  useEffect(() => { if (armed) onIntentHandled?.(); }, [armed, onIntentHandled]);

  if (phase === 'loading') return <p className="muted">Loading your accounts…</p>;

  if (phase === 'failed' || !accounts) {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your accounts</h3>
        <p className="muted">
          Nothing has been changed. This is a read problem, not a missing-accounts
          problem — your accounts are still there.
        </p>
        {error ? <p className="muted"><code>{error}</code></p> : null}
        <button type="button" className="btn primary" onClick={reload}>Try again</button>
      </div>
    );
  }

  if (!accounts.length) {
    return (
      <div className="card">
        <h3>No accounts yet</h3>
        <p className="muted">
          Add an account to route imported trades and track prop-firm rules.
        </p>
        {/* ⚠ THE EMPTY STATE NEEDS THE BUTTON MORE THAN THE POPULATED ONE DOES.
            It used to read "adding accounts is not ported yet — use the current
            client", which stopped being true the moment the modal landed. A
            first-run user reaching this screen has exactly one thing to do. */}
        <NewAccountButton autoOpen={armed} />
      </div>
    );
  }

  return <AccountsBoard accounts={accounts} go={go} />;
}

// ---------------------------------------------------------------------------
// The board
// ---------------------------------------------------------------------------

function AccountsBoard(
  { accounts, go }: { accounts: readonly Account[]; go?: ((tab: string) => void) | undefined },
) {
  // 🚨 Accounts is FREE — the free tier includes one account — and this board
  // carries a Coach Me button, so the tab gate never touches it (#76).
  const coach = useProAction('ai-coach');
  const [coachRefused, setCoachRefused] = useState(false);
  const { phase, trades, error, today, loadWholeBook } = useJournal();
  const { prefs, reload: reloadPrefs } = usePreferences();
  const { reload } = useAccounts();
  const mode: DayBoundaryMode | null = prefs?.dayBoundaryMode ?? null;

  const [filter, setFilter] = useState<CardFilter>('all');
  const [sort, setSort] = useState<CardSort>('risk');
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /**
   * The account whose Risk of Ruin simulator is open.
   *
   * ⚠ HELD AS AN ID, RESOLVED TO A MODEL AT RENDER. Holding the model itself
   * would freeze the simulation's inputs at the moment it opened, so a trade
   * landing from the logger while the modal is up would move the card behind it
   * and leave the modal quoting a balance that no longer exists.
   */
  const [simulating, setSimulating] = useState<string | null>(null);
  /**
   * The Compare Accounts brief, once it has been built.
   *
   * 🚨 IT IS BUILT ON THE PRESS, NOT ON EVERY RENDER. The brief needs a week of
   * trades with their mistake tags and behaviour flags, which the tab does not
   * otherwise read - and fetching those on mount would put a request on every
   * visit to this screen for a feature most visits never use.
   *
   * ⚠ Building it costs nothing. SENDING it is what bills, and that happens
   * inside the modal, behind its own button.
   */
  const [comparing, setComparing] = useState<{ brief: string; cacheKey: string } | null>(null);
  const [comparingBusy, setComparingBusy] = useState(false);
  const [comparingError, setComparingError] = useState<string | null>(null);
  const { patterns } = usePatterns();
  const editingAccount = editing ? accounts.find((a) => a.id === editing) ?? null : null;

  /**
   * The delete flow's state.
   *
   * ⚠ THE IMPACT IS COUNTED WHEN THE DIALOG OPENS, not when Delete is pressed,
   * and the confirm button stays disabled until it lands. Confirming a delete
   * whose consequences are unknown is the one thing the dialog exists to stop.
   */
  const { sb, userId } = useSupabase();
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const [impact, setImpact] = useState<DeleteImpact | null>(null);
  const [countError, setCountError] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const askDelete = useCallback((id: string, name: string) => {
    setDeleting({ id, name });
    setImpact(null);
    setCountError(null);
    setDeleteError(null);
    if (!sb) return;
    // ⚠ THE `.catch` IS NOT DEFENSIVE PADDING. `countDeleteImpact` returns its
    // PostgREST errors, but a thrown one — an offline fetch, a bad client —
    // rejects instead, and without this the dialog sits on "Checking what this
    // would affect…" for ever with Delete disabled and nothing explaining why.
    // A stuck dialog is the one failure mode worse than a blunt error, because
    // there is no way out of it but a reload. Found by an unhandled rejection in
    // the test run, not by review.
    void countDeleteImpact(sb, id)
      .then((r) => { if (r.error) setCountError(r.error); else setImpact(r.impact); })
      .catch((e: unknown) => {
        setCountError(
          `Couldn’t check what this would affect: ${reportFailure('check delete impact', e)}.`,
        );
      });
  }, [sb]);

  const closeDelete = useCallback(() => {
    setDeleting(null);
    setImpact(null);
    setCountError(null);
    setDeleteError(null);
  }, []);

  /**
   * Saving an account's rules as a reusable template.
   *
   * ⚠ THE FULL `Account` IS WHAT GETS CAPTURED, not the card's `CardAccount`.
   * The card model narrows `rules` to the ten keys a gauge needs; a template has
   * to carry all of them, including `dllEnforcement` and `consistencyScope` —
   * the pair that two engines once defaulted in opposite directions when they
   * went missing.
   */
  const [savingTemplate, setSavingTemplate] = useState<Account | null>(null);
  const [tplBusy, setTplBusy] = useState(false);
  const [tplError, setTplError] = useState<string | null>(null);
  /** The confirmation line, kept AFTER the dialog closes — see below. */
  const [tplSaved, setTplSaved] = useState<string | null>(null);

  const storedTemplates = useMemo(
    () => parsePropTemplates(prefs?.propTemplates ?? []), [prefs],
  );

  const askSaveTemplate = useCallback((id: string) => {
    const full = accounts.find((a) => a.id === id) ?? null;
    setSavingTemplate(full);
    setTplError(null);
    setTplSaved(null);
  }, [accounts]);

  const saveTemplate = useCallback((label: string) => {
    if (!sb || !userId || !savingTemplate) return;
    setTplBusy(true);
    setTplError(null);

    const tpl = buildPropTemplate({
      account: savingTemplate,
      label,
      // ⚠ Minted here rather than in `domain/`, which reads no clock and makes no
      // random ids (D26) — the same split `createAccount` uses.
      id: USER_TEMPLATE_PREFIX + newAccountId(),
      savedAt: new Date().toISOString(),
    });

    void savePropTemplate(sb, userId, tpl)
      .then((r) => {
        setTplBusy(false);
        if (!r.ok) { setTplError(r.error ?? 'That did not save.'); return; }
        setSavingTemplate(null);
        // 🚨 SAY WHICH OUTCOME HAPPENED. A save that silently replaced an
        // existing template of the same name is the surprise this whole flow was
        // built to avoid — the dialog warns first, and this confirms after.
        setTplSaved(
          r.replacedId
            ? `Replaced “${tpl.label}” with these rules — pick it when you create your next account.`
            : `Saved “${tpl.label}” — pick it when you create your next account.`,
        );
        // The setup modal reads the list from preferences, so it has to re-read.
        reloadPrefs();
      })
      .catch((e: unknown) => {
        // ⚠ The same lesson D140 paid for: a thrown fetch rejects rather than
        // returning, and without this the dialog sits on "Saving…" for ever.
        setTplBusy(false);
        setTplError(`That did not save: ${reportFailure('save account template', e)}`);
      });
  }, [sb, userId, savingTemplate, reloadPrefs]);

  const openCompare = useCallback(async () => {
    if (!sb) return;
    setComparingBusy(true);
    setComparingError(null);
    const from = weekStartOf(today);
    try {
      const rows = await loadComparisonWeek(sb, from, today);
      const names: Record<string, string> = {};
      for (const pat of patterns ?? []) names[String(pat.id)] = pat.name;
      const brief = buildComparisonBrief({
        // ⚠ EVERY account, archived included. "I stopped trading that one" is
        // a cross-account finding, and dropping the quiet accounts hides it.
        accounts: accounts.map((a) => ({
          id: String(a.id),
          name: a.name || 'Untitled',
          firm: a.firm || null,
          accountType: a.accountType || null,
          status: a.status || null,
          maxDrawdown: Number(a.rules?.maxDDValue) || null,
          maxDrawdownType: a.rules?.maxDDType || null,
          drawdownRoom: null,
          archived: a.archived === true,
        })),
        trades: rows,
        weekStart: from,
        weekEnd: today,
        patternNames: names,
      });
      setComparing({ brief, cacheKey: `acctcmp:${from}` });
    } catch (e) {
      setComparingError(reportFailure('compare accounts', e));
    } finally {
      setComparingBusy(false);
    }
  }, [sb, today, accounts, patterns]);

  // ⚠ ONCE PER MOUNT, and the ref is what makes it once. `loadWholeBook` returns
  // the phase to `windowed` when a backfill FAILS, so an effect keyed only on
  // the phase would fire again immediately and retry for ever. The shell
  // remounts this subtree on navigation (D94), so leaving and returning is a
  // deliberate second attempt — which is the behaviour a failed read wants.
  const asked = useRef(false);
  useEffect(() => {
    if (asked.current || phase !== 'windowed') return;
    asked.current = true;
    loadWholeBook();
  }, [phase, loadWholeBook]);

  const whole = trades && isWholeBook(trades) ? trades : null;

  const models = useMemo<AccountCardModel[] | null>(() => {
    if (!whole || !mode) return null;
    return accounts.map((a) => buildAccountCardModel(
      a as unknown as CardAccount, whole, today, mode,
    ));
  }, [accounts, whole, today, mode]);

  if (!models) {
    return (
      <div className="accounts-tab">
        <HistoryGate phase={phase} error={error} trades={trades} onRetry={loadWholeBook} />
        <IdentityList accounts={accounts} />
        <NotWiredHere />
      </div>
    );
  }

  // ⚠ ARCHIVED SPLITS OUT OF THE ACTIVE VIEW ONLY. It still counts in every
  // metric elsewhere unless the account is separately excluded — the group's own
  // note says so on screen, because "archived" reading as "deleted" is exactly
  // the misunderstanding that made the old client drop 704 of 716 trades from
  // its Coach's Report.
  const live = models.filter((m) => !m.a.archived);
  const archived = models.filter((m) => m.a.archived);
  const counts = cardFilterCounts(live);
  const shown = sortAccountCards(live.filter((m) => cardMatchesFilter(m, filter)), sort);
  const agg = buildHouseAggregate(live, whole!, today, weekStartOf(today), mode!);
  const simulatingModel = simulating
    ? models.find((m) => m.a.id === simulating) ?? null : null;

  return (
    <div className="accounts-tab">
      <div className="acc-head-row">
        <h2>Accounts ({accounts.length})</h2>
        <span className="grow" />
        {/* ⚠ ON THE TITLE ROW, WHICH IS WHERE BETA PUTS THEM (#73). The comment
            that used to sit at the old position claimed beta "pins it to the
            right of the same row" as the filter and sort chips — it does not,
            and a screenshot settled it: both buttons sit beside "Accounts (15)".
            The argument for the chip row was that a WRITE among view controls
            should not come first; on the title row that argument does not apply,
            because the title row holds no view controls at all. */}
        {/* 🚨 ONLY WITH TWO OR MORE ACCOUNTS. A comparison of one account is not
            a comparison, and the old client hides the button for the same
            reason rather than showing one that answers nothing. */}
        {accounts.length >= COMPARISON_MIN_ACCOUNTS ? (
          <button type="button" className="btn acc-compare" disabled={comparingBusy}
            onClick={() => {
              // ⚠ Before `openCompare`, which reads the week for every account.
              if (coach.blocked) { setCoachRefused(true); return; }
              void openCompare();
            }}>
            {comparingBusy ? 'Reading your week…' : 'Coach Me — Compare Accounts'}
          </button>
        ) : null}
        <button type="button" className="btn primary acc-new" onClick={() => setCreating(true)}>
          + New Account
        </button>
      </div>

      {/* ⚠ ABOVE the house banner, because it is a statement about whether the
          numbers below it can be trusted at all. A conflict means that account's
          figures for those days are double-counted, so it has to be read first. */}
      <MultiSourceNotice />

      {/* 🚨 THE SAME REASONING, ONE STEP FURTHER IN (#73). MultiSourceNotice says
          two SOURCES wrote one account-day; this says one TRADE is in the book
          twice. Both inflate everything that counts trades, and both belong
          above the numbers they undermine.
          ⚠ Josh's book had three of these on 2026-09-04, and this client could
          neither see nor remove them — the reason the tool moved to the top of
          #73 ahead of every cosmetic item on it. */}
      <DuplicateNotices accounts={accounts} onMerged={reload} nameOf={(id) =>
        accounts.find((a) => String(a.id) === id)?.name ?? 'Account'} />

      <HouseBanner agg={agg} fundedCount={counts.funded} />

      {/* ⚠ OUTLIVES THE DIALOG THAT CAUSED IT. The old client uses a toast that
          fades after four seconds; a confirmation that says a template was
          REPLACED is the one the trader most needs to still be there when they
          look up. It dismisses on click, not on a timer. */}
      {/* ⚠ The week read failing has to be visible. Without this the button
          would go busy, come back, and open nothing. */}
      {comparingError ? (
        <div className="acc-notice red" role="alert">
          <span>Couldn't read this week's trades, so there is nothing to compare yet: {comparingError}</span>
          <button type="button" className="linklike" onClick={() => setComparingError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      {tplSaved ? (
        <div className="acc-notice" role="status">
          <span>{tplSaved}</span>
          <button type="button" className="linklike" onClick={() => setTplSaved(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="acc-controls">
        <div className="group">
          <span className="label">Show:</span>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`acc-chip${filter === f.key ? ' active' : ''}`}
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >{f.label} ({num(counts[f.key], 0)})</button>
          ))}
        </div>
        <div className="grow" />
        <div className="group">
          <span className="label">Sort:</span>
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`acc-chip${sort === s.key ? ' active' : ''}`}
              aria-pressed={sort === s.key}
              onClick={() => setSort(s.key)}
            >{s.label}</button>
          ))}
        </div>
      </div>

      {shown.length ? (
        <div className="account-grid">
          {shown.map((m) => <AccountCard key={m.a.id} m={m} onEdit={setEditing} onArchived={reload} onDelete={askDelete}
              onSaveTemplate={askSaveTemplate} onSimulate={setSimulating} />)}
        </div>
      ) : (
        <div className="card">
          <p className="muted">
            {live.length
              ? 'No accounts match the current filter.'
              : 'No active accounts — every one of them is archived (see below).'}
          </p>
        </div>
      )}

      {archived.length ? (
        <div className="acc-archived-section">
          <button
            type="button"
            className="acc-archived-head"
            aria-expanded={archivedOpen}
            onClick={() => setArchivedOpen((v) => !v)}
          >
            <span className="chev" aria-hidden="true">{archivedOpen ? '▾' : '▸'}</span>
            <span className="label">Archived ({num(archived.length, 0)})</span>
            <span className="note">
              out of the way — still count in metrics unless separately excluded
            </span>
          </button>
          {archivedOpen ? (
            <div className="account-grid acc-archived-grid">
              {archived.map((m) => <AccountCard key={m.a.id} m={m} onEdit={setEditing} onArchived={reload} onDelete={askDelete}
              onSaveTemplate={askSaveTemplate} onSimulate={setSimulating} />)}
            </div>
          ) : null}
        </div>
      ) : null}

      <NotWiredHere />

      {creating ? (
        <AccountSetupModal
          onCancel={() => setCreating(false)}
          onCreated={() => { setCreating(false); reload(); }}
        />
      ) : null}

      {deleting ? (
        <DeleteAccountDialog
          name={deleting.name}
          impact={impact}
          countError={countError}
          busy={deleteBusy}
          error={deleteError}
          onCancel={closeDelete}
          // ⚠ The reversible way out, offered from inside the destructive
          // dialog. Most people reaching for Delete want the card off the list.
          onArchiveInstead={() => {
            if (!sb) return;
            setDeleteBusy(true);
            void setAccountArchived(sb, deleting.id, true).then((r) => {
              setDeleteBusy(false);
              if (!r.ok) { setDeleteError(r.error ?? 'That did not save.'); return; }
              closeDelete();
              reload();
            });
          }}
          onConfirm={() => {
            if (!sb || !impact) return;
            setDeleteBusy(true);
            setDeleteError(null);
            void deleteAccount(sb, impact).then((r) => {
              setDeleteBusy(false);
              if (!r.ok) { setDeleteError(r.error ?? 'The delete failed.'); return; }
              closeDelete();
              reload();
            });
          }}
        />
      ) : null}

      {savingTemplate ? (
        <SaveTemplateDialog
          accountName={savingTemplate.name || 'Untitled'}
          firm={savingTemplate.firm}
          accountSize={savingTemplate.accountSize}
          rules={savingTemplate.rules}
          suggested={suggestTemplateLabel(savingTemplate)}
          existing={storedTemplates}
          busy={tplBusy}
          error={tplError}
          onCancel={() => { setSavingTemplate(null); setTplError(null); }}
          onSave={saveTemplate}
        />
      ) : null}

      {editingAccount ? (
        <AccountEditor
          account={editingAccount}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      ) : null}

      {/* ⚠ Looked up in the LIVE model list, so it closes itself if the account
          it is simulating disappears — deleted from the menu behind it, or
          archived out of the current filter. */}
      {simulatingModel ? (
        <RiskOfRuinModal m={simulatingModel} onClose={() => setSimulating(null)} />
      ) : null}

      {coachRefused ? (
        <ProActionLimit
          title={PRO_ACTION_LABEL['ai-coach']}
          message={coach.blocked ?? ''}
          pitch={PRO_ACTION_PITCH['ai-coach']}
          onClose={() => setCoachRefused(false)}
          onSeePlans={go ? () => go('account') : undefined}
        />
      ) : null}

      {comparing ? (
        <CompareAccountsModal
          brief={comparing.brief}
          cacheKey={comparing.cacheKey}
          accountCount={accounts.length}
          weekStart={weekStartOf(today)}
          weekEnd={today}
          onClose={() => setComparing(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// While the history loads
// ---------------------------------------------------------------------------

/**
 * Says WHY the cards are not here yet, once, above the list.
 *
 * ⚠ Without this the held numbers read as a bug. "Not loaded" and "broken" look
 * identical, and the person most likely to be looking is the one checking this
 * screen against the old client.
 */
function HistoryGate(
  { phase, error, trades, onRetry }: {
    phase: string;
    error: string | null;
    trades: Population<MetricsRow> | null;
    onRetry: () => void;
  },
) {
  const running = phase === 'booting' || phase === 'backfilling';
  // A backfill that failed drops back to `windowed` with an error set, and the
  // window itself is still fine — so this is the only place that difference is
  // visible to the trader.
  const failed = !running && !!trades && !!error;

  return (
    <div className="card" role="status">
      <h3>{failed ? 'Couldn’t load your full history' : 'Reading your full history'}</h3>
      <p className="muted">
        Balance, drawdown room, profit target, consistency and days traded read
        <b> every trade you have ever taken</b>, not the last few weeks. Computed
        from a partial history they come out <b>too generous</b> — more room than
        you have — which is the dangerous direction, so they are held until the
        read finishes rather than shown with a caveat.
      </p>
      {failed ? (
        <>
          <p className="muted"><code>{error}</code></p>
          <button type="button" className="btn primary" onClick={onRetry}>Try again</button>
        </>
      ) : (
        <p className="muted">This runs once per visit and takes a moment.</p>
      )}
    </div>
  );
}

/**
 * Identity and rules only — the part that needs no trades at all.
 *
 * ⚠ Everything here comes straight off the accounts row. Nothing on this list
 * is computed from the trade history, which is the whole reason it can render
 * before the read lands.
 */
function IdentityList({ accounts }: { accounts: readonly Account[] }) {
  return (
    <>
      {accounts.map((a) => (
        <section className="card" key={a.id}>
          <h3>{a.name}</h3>
          <p className="muted">
            {a.firm ? `${a.firm} · ` : ''}{a.accountType}
            {a.status !== 'active' ? ` · ${a.status}` : ''}
            {a.externalId ? ` · matches “${a.externalId}” on import` : ''}
          </p>
          {/* ⚠ `moneyPlain`, NOT `money`. A balance is a LEVEL, not a result:
              `money` signs its output, and "+$51,200" reads as a gain of fifty
              thousand dollars rather than the balance on the account. PD-3 is
              the family of bugs that live in exactly this choice. */}
          <dl className="kv">
            <Row label="Account size" value={moneyPlain(a.accountSize, 0)} />
            <Row label="Starting balance" value={moneyPlain(a.startingBalance, 0)} />
            {/* The STORED figure, not a computed one. It comes off the accounts
                row and needs no trades; the computed display balance walks the
                trade timeline and is held with everything else. */}
            <Row label="Balance on file" value={moneyPlain(a.currentBalance, 0)} />
            {describeRules(a).map((r) => <Row key={r.label} label={r.label} value={r.value} />)}
          </dl>
        </section>
      ))}
    </>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt className="muted">{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

/** Only the rules that are actually set. A rule of 0 means "none", and listing
 *  every unset rule as "0" reads as the strictest possible account. */
function describeRules(a: Account): { label: string; value: string }[] {
  const r = a.rules;
  const out: { label: string; value: string }[] = [];
  if (r.maxDDValue) out.push({ label: `Max drawdown${r.maxDDType ? ` (${r.maxDDType})` : ''}`, value: moneyPlain(r.maxDDValue, 0) });
  if (r.dailyLossLimit) out.push({ label: 'Daily loss limit', value: moneyPlain(r.dailyLossLimit, 0) });
  if (r.profitTarget) out.push({ label: 'Profit target', value: moneyPlain(r.profitTarget, 0) });
  if (r.consistencyRulePct) out.push({ label: 'Consistency rule', value: `${r.consistencyRulePct}%` });
  if (r.minTradingDays) out.push({ label: 'Min trading days', value: String(r.minTradingDays) });
  if (r.maxContracts) out.push({ label: 'Max contracts', value: String(r.maxContracts) });
  return out;
}

// ---------------------------------------------------------------------------
// The house-health banner
// ---------------------------------------------------------------------------

/**
 * Two loggers writing to one account.
 *
 * ── 🚨 THE DETECTOR WAS BUILT ON 2026-08-09 AND NOTHING CALLED IT UNTIL NOW ──
 *
 * `domain/multiSource.ts`, verified against the real book — 8 account-days
 * across 6 accounts — with its own test as its ONLY reference anywhere in the
 * codebase. Sixth instance of this port's most common defect class (D149, D151,
 * D152, D157, D203). ⚠ It stayed unwired for a good reason: where the warning
 * belongs was a product decision, and this port has invented three controls beta
 * does not have. Josh chose the Accounts tab.
 *
 * ── ⚠ IT SAYS "YOUR NUMBERS ARE WRONG", NOT "DELETE THESE ROWS" ──
 *
 * The detector CANNOT know which logger is right — that is a question about the
 * trader's setup, not about the data. And `extraRows` / `extraLots` are an
 * ESTIMATE that assumes the biggest source is the complete one, which is a
 * guess: on 2026-08-05 the SMALLER source held the day's largest loss. So the
 * copy sizes the problem and stops. D148 is explicit that it is "offered to size
 * the problem, never to decide what to delete."
 *
 * ⚠ NOTHING RENDERS WHEN THE BOOK IS CLEAN. An all-clear card would be a
 * permanent reassurance nobody reads, and it would compete with the warning for
 * the one place a warning can live.
 */
function MultiSourceNotice() {
  const { phase, days, summary, error } = useMultiSource();

  // ⚠ A FAILED CHECK IS NOT AN ALL-CLEAR, and this is the branch that keeps them
  // apart. An empty `days` is the good news; a null one means nobody looked.
  if (phase === 'failed') {
    return (
      <div className="acc-notice" role="status">
        <span className="muted">
          Couldn’t check whether two loggers are writing to the same account. {error}
        </span>
      </div>
    );
  }

  if (phase === 'loading' || !days || !summary || days.length === 0) return null;

  const accounts = summary.accounts.length;
  const sources = summary.sources.join(' and ');

  return (
    <div className="acc-notice red" role="alert">
      <span>
        <b>
          {sources} both wrote to {accounts === 1 ? 'one account' : `${accounts} accounts`}
          {' '}on {summary.days === 1 ? 'one day' : `${summary.days} days`}.
        </b>
        {' '}Those days are counted twice, so the balance, P&amp;L and drawdown for
        {' '}{accounts === 1 ? 'that account' : 'those accounts'} are wrong —
        {' '}by roughly {summary.extraLots} contract{summary.extraLots === 1 ? '' : 's'}
        {' '}across {summary.extraRows} trade{summary.extraRows === 1 ? '' : 's'}.
        {summary.latest ? ` Most recent: ${summary.latest}.` : ''}
        {/* ⚠ Names the FIX, which is a setup change, and stops there. It cannot
            say which source is right, so it must not imply it can. */}
        {' '}The fix is to run one logger per account. Which of the two is correct
        {' '}depends on your setup, so nothing here has been changed or removed.
      </span>
      <details className="acc-ms-days">
        <summary>Show the {summary.days === 1 ? 'day' : 'days'}</summary>
        <ul>
          {days.map((d) => (
            <li key={`${d.account}-${d.date}`}>
              <span className="mono">{d.date}</span> · {d.account} ·{' '}
              {d.sources.map((t) => `${t.source} ${t.rows}r/${t.lots}l`).join('  vs  ')}
            </li>
          ))}
        </ul>
        {/* ⚠ The estimate says so, in the one place someone might act on it. */}
        <p className="muted">
          The excess is an estimate: it assumes the source with the most rows is
          the complete one. That is a guess — on at least one day in this book the
          smaller source held the largest loss.
        </p>
      </details>
    </div>
  );
}

/**
 * The same trade written into one account twice (#73).
 *
 * ⚠ SILENT WHILE SCANNING AND SILENT ON FAILURE. See `useDuplicateScan` for why
 * the read fails quiet on this screen — and why the TOOL does not.
 *
 * ⚠ IT RE-COUNTS AFTER A REMOVAL rather than assuming the removal emptied it. A
 * partial delete leaves some groups standing, and a banner that vanished on a
 * half-finished job would tell the trader their book was clean.
 */
function DuplicateNotices(
  { accounts, nameOf, onMerged }: {
    accounts: readonly Account[];
    nameOf: (id: string) => string;
    onMerged: () => void;
  },
) {
  // 🚨 ONE READ, TWO TOOLS. Both banners need the same nine columns over the
  // whole book — one to group identical trades, the other to count trades per
  // account and to decide which of a losing copy's trades the keeper already
  // has. Two hooks would mean two whole-book reads on every visit to this tab.
  const { rows, groups, excess, rescan } = useDuplicateScan();
  const [open, setOpen] = useState(false);
  const [merging, setMerging] = useState<number | null>(null);

  const candidateIds = useMemo(
    () => groups.flatMap((g) => g.trades.map((t) => t.id)), [groups],
  );

  /**
   * Accounts whose names collide (#73).
   *
   * ⚠ DORMANT ON JOSH'S BOOK, and that is the expected state rather than a
   * failure: his accounts have distinct names, so this finds nothing. It was
   * confirmed from his own screenshot on 2026-09-04, where only the trade banner
   * was showing.
   */
  const dupeAccounts = useMemo(
    () => detectDuplicateAccounts(
      accounts.map((a) => ({
        id: String(a.id), name: String(a.name ?? ''), firm: a.firm ?? null,
      })),
    ),
    [accounts],
  );

  if (!groups.length && !dupeAccounts.length) return null;

  return (
    <>
      {dupeAccounts.map((g, i) => (
        <div className="acc-notice gold" role="alert" key={g[0]?.id ?? i}>
          <span>
            <b>
              {g.length === 2
                ? '2 accounts share a name'
                : `${g.length} accounts share a name`}
            </b>
            {' '}— usually one account imported from two platforms, with your
            trades <b>split across the copies</b>, so every figure on each is
            only part of the picture.
          </span>
          <button type="button" className="btn primary"
            onClick={() => setMerging(i)}>Review &amp; Merge</button>
        </div>
      ))}
      {merging !== null && dupeAccounts[merging] ? (
        <MergeAccountsModal
          group={dupeAccounts[merging]!}
          rows={rows}
          onClose={() => setMerging(null)}
          onMerged={() => { onMerged(); rescan(); }}
        />
      ) : null}

      {groups.length ? (
      <div className="acc-notice gold" role="alert">
        <span>
          <b>
            {groups.length === 1
              ? '1 possible duplicate trade group detected'
              : `${groups.length} possible duplicate trade groups detected`}
          </b>
          {' '}— the same trade logged more than once in an account (often a
          double-import), <b>which inflates your stats</b>.
          {' '}
          {/* ⚠ POSSIBLE. Two same-minute scalps at one price are indistinguishable
              from a double-import here, and the tool asks per group rather than
              stating a number of duplicates it cannot know. */}
          {excess === 1 ? 'One row' : `Up to ${excess} rows`} would be removed.
        </span>
        <button type="button" className="btn primary"
          onClick={() => setOpen(true)}>Review &amp; Remove</button>
      </div>
      ) : null}
      {open ? (
        <DedupeTradesModal
          candidateIds={candidateIds}
          accountName={nameOf}
          onClose={() => setOpen(false)}
          onRemoved={() => { rescan(); }}
        />
      ) : null}
    </>
  );
}

function HouseBanner({ agg, fundedCount }: { agg: HouseAggregate; fundedCount: number }) {
  const ruinTone = agg.worstRuin == null ? ''
    : agg.worstRuin > 20 ? ' red' : agg.worstRuin > 10 ? ' gold' : ' green';

  return (
    <div className="agg-banner">
      <div className={`agg-health${agg.health === 'ok' ? '' : ` ${agg.health}`}`}>
        <div className="dot" aria-hidden="true" />
        <div>
          <div className="label">House Health</div>
          <div className="state">{agg.healthLabel}</div>
        </div>
      </div>
      <div className="agg-cell">
        <div className="slabel">Funded $</div>
        <div className="sval">{moneyPlain(agg.fundedBalance, 0)}</div>
        <div className="ssub">across {num(fundedCount, 0)} account{fundedCount === 1 ? '' : 's'}</div>
      </div>
      <div className="agg-cell">
        <div className="slabel">Today’s P&L</div>
        <div className={`sval ${signClass(agg.todayPnl)}`}>{money(agg.todayPnl, 0)}</div>
        <div className="ssub">net across all</div>
      </div>
      <div className="agg-cell">
        <div className="slabel">Week P&L</div>
        <div className={`sval ${signClass(agg.weekPnl)}`}>{money(agg.weekPnl, 0)}</div>
        <div className="ssub">last 7 days</div>
      </div>
      <div className="agg-cell">
        <div className="slabel">Worst RoR</div>
        <div className={`sval${ruinTone}`}>
          {agg.worstRuin == null ? '—' : `${num(agg.worstRuin, 1)}%`}
        </div>
        <div className="ssub">
          {agg.worstRuinAccount ?? (agg.worstRuin == null ? 'insufficient data' : '')}
        </div>
      </div>
      <div className="agg-cell">
        <div className="slabel">DD Buffer</div>
        <div className="sval">{moneyPlain(agg.ddBuffer, 0)}</div>
        <div className="ssub">total room before bust</div>
      </div>
    </div>
  );
}

/** ⚠ Zero is neither. Colouring a flat day green claims a result it did not
 *  have, and PD-3 is the family of bugs that live in exactly this helper. */
function signClass(n: number): string {
  if (n > 0) return 'green';
  if (n < 0) return 'red';
  return 'muted';
}

// ---------------------------------------------------------------------------
// One card
// ---------------------------------------------------------------------------

/**
 * `TOPSTEP · combine · $50,000 · $165`, minus whatever is not set.
 *
 * ⚠ The COST is on this line deliberately: the money is visible without opening
 * anything and reads as ordinary account metadata rather than an accounting
 * chore. A WAIVED fee says "free" — a different fact from no fee recorded.
 */
function firmLine(a: CardAccount): string {
  const parts = [
    (a.firm || '').toUpperCase(),
    a.accountType,
    moneyPlain(a.accountSize, 0),
    Number(a.feeAmount) > 0
      ? `${moneyPlain(a.feeAmount, 0)}${a.feeCadence === 'monthly' ? '/mo' : ''}`
      : (a.feeWaived === true ? 'free' : ''),
  ];
  return parts.filter(Boolean).join(' · ');
}

function AccountCard(
  { m, onEdit, onArchived, onDelete, onSaveTemplate, onSimulate }:
  {
    m: AccountCardModel;
    onEdit: (id: string) => void;
    onArchived: () => void;
    onDelete: (id: string, name: string) => void;
    onSaveTemplate: (id: string) => void;
    onSimulate: (id: string) => void;
  },
) {
  const a = m.a;
  const statusLabel = m.locked ? 'Locked' : lifecycleLabel(m.lifecycle);
  const pillClass = m.locked ? 'locked' : m.lifecycle;

  // ── the ✕ on the missing-fields prompt (#73) ───────────────────────────────
  // ⚠ HIDDEN LOCALLY ON SUCCESS rather than by re-reading every account. The
  // flag changes nothing else on this card or any other, so a full accounts
  // reload would be a network round trip to redraw one line — and it would make
  // the prompt flicker back while it ran.
  const { sb } = useSupabase();
  const [dismissedHere, setDismissedHere] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);
  const dismissNudge = useCallback(async () => {
    setDismissing(true);
    setDismissError(null);
    const res = await dismissSetupNudge(sb, String(a.id));
    setDismissing(false);
    if (res.ok) setDismissedHere(true);
    else setDismissError(res.error ?? 'That prompt could not be dismissed.');
  }, [sb, a.id]);

  return (
    <section
      className={`account-card ${m.signal}${m.terminal ? ' terminal' : ''}`}
      data-account={a.id}
      data-signal={m.signal}
    >
      <div className="ac-head">
        <div className="name-block">
          <div className="name">
            {a.name || 'Untitled'}
            {m.terminal ? (
              <span className="ac-final-badge">
                {m.lifecycle === 'passed' ? 'AT PASS' : m.lifecycle === 'blown' ? 'AT BLOW' : 'AT CLOSE'}
              </span>
            ) : null}
          </div>
          {/* ⚠ JOINED, not concatenated with separators between fixed slots.
              Three of Josh's accounts have no firm, and the old client's
              template leaves the separator behind: " · combine · $50,000". */}
          <div className="firm-line">{firmLine(a)}</div>
        </div>
        <div className="actions">
          {a.excludeFromMetrics ? (
            <span className="acct-excl-badge" title="Its trades are listed but move no number">
              Excluded
            </span>
          ) : null}
          <span className={`status-pill ${pillClass}`}>
            <span className="dot" aria-hidden="true" />{statusLabel}
          </span>
          <CardMenu
            accountId={a.id}
            archived={a.archived}
            onEdit={() => onEdit(a.id)}
            onArchived={onArchived}
            onDelete={() => onDelete(a.id, a.name || 'Untitled')}
            // ⚠ HIDDEN, not disabled, when there is nothing to save. A SIM or
            // personal account has no drawdown, target or daily limit, so the
            // entry would mint an all-zeros template that then sits in the
            // picker looking exactly like a real one.
            canSaveTemplate={accountHasSaveableRules(a)}
            onSaveTemplate={() => onSaveTemplate(a.id)}
          />
        </div>
      </div>

      <CardBanner m={m} />

      <div className="compliance-gauges">
        {m.gauges.map((g) => <GaugeTile key={g.key} g={g} />)}
      </div>

      <ConsistencyCallout m={m} />

      <div className="bars">
        <TodayRoom m={m} />
        <ConsistencyBar m={m} />
      </div>

      <div className="card-footer-pin">
        <RuinStrip m={m} onOpen={() => onSimulate(a.id)} />
        {/* ⚠ THE ✕ IS BACK, AND SO IS HONOURING A DISMISSAL MADE IN BETA (#73).
            The note that used to sit here said the ✕ stayed out because its flag
            "rides a field this client does not write" — true then, and it left
            the worse half unsaid: this client did not READ it either, so a
            prompt Josh had already waved away in the shipping client came back
            on every card here. Both clients read one database.
            ⚠ Gated at the RENDER, not inside `accountSetupMissing` — the list of
            what is missing stays true, and other readers ask that question. */}
        {m.missing.length && !a.setupNudgeDismissed && !dismissedHere ? (
          <div className="acct-nudge">
            <b>{num(m.missing.length, 0)} field{m.missing.length === 1 ? '' : 's'} missing</b>
            <span className="an-list">— {m.missing.join(', ')}</span>
            <button type="button" className="linklike" onClick={() => onEdit(a.id)}>
              Finish setup →
            </button>
            <button
              type="button"
              className="acct-nudge-x"
              title="Dismiss this prompt for this account"
              aria-label={`Dismiss the setup prompt for ${a.name || 'this account'}`}
              disabled={dismissing}
              onClick={() => { void dismissNudge(); }}
            >×</button>
          </div>
        ) : null}
        {/* ⚠ A FAILED DISMISS SAYS SO. The write is small and invisible — the
            prompt simply would not go away — and a silent failure there reads as
            a button that does nothing. */}
        {dismissError ? (
          <p className="muted" role="alert">{dismissError}</p>
        ) : null}
      </div>

      {/* ⚠ OUTSIDE the pinned footer, as the old client has it. A closed account
          has no live gauge left to read, so its result is the one thing worth
          keeping — and it belongs after the block that absorbs the card's slack,
          not inside it. */}
      {m.terminal ? (
        <div className="ac-final-summary">
          <span className="label">Final Net P&L</span>
          <span className={`value ${signClass(m.pnlVsStart)}`}>{money(m.pnlVsStart, 0)}</span>
        </div>
      ) : null}
    </section>
  );
}

/**
 * The one banner across the top of a card, and only one.
 *
 * Locked outranks a failing drawdown, which outranks a thin one. A terminal
 * account gets none at all — the pill carries the message, and a "cut size or
 * pause" nudge on a closed account is advice about a thing that cannot happen.
 */
function CardBanner({ m }: { m: AccountCardModel }) {
  if (m.locked) {
    return (
      <div className="locked-banner">
        <span className="icon" aria-hidden="true">⛔</span>
        <div className="body">
          <b>Daily loss limit hit.</b> No new trades on this account until it
          resets (06:00 ET tomorrow).
        </div>
      </div>
    );
  }
  if (m.lifecycle !== 'active') return null;
  const state = m.compliance?.state;
  if (state !== 'danger' && state !== 'failed') return null;
  return (
    <div className="locked-banner amber">
      <span className="icon" aria-hidden="true">⚠</span>
      <div className="body">
        {state === 'failed'
          ? <><b>Drawdown floor breached.</b> The balance is under the trailing
            floor — by these rules the account is failed.</>
          : <>Drawdown remaining is {num(m.ddPct, 0)}% of max. Consider cutting
            size or pausing.</>}
      </div>
    </div>
  );
}

/**
 * One gauge.
 *
 * ⚠ The arc is a fixed-length path with a dash offset, relocated from the old
 * client including the 110-unit length its `stroke-dasharray` assumes. Changing
 * the path without changing that constant leaves every gauge reading a different
 * percentage from the one beneath it.
 */
function GaugeTile({ g }: { g: GaugeSlot }) {
  const stroke = g.state === 'healthy' || g.state === 'achieved' ? 'var(--green)'
    : g.state === 'caution' ? 'var(--gold)'
      : g.state === 'danger' || g.state === 'failed' ? '#ef4444'
        : 'var(--text-3)';
  const showArc = !g.unset && g.state !== 'not-started';

  return (
    <div className={`gauge-tile ${g.state}${g.unset ? ' unset' : ''}`}>
      <div className="gauge-label">{g.label}</div>
      <div className="gauge-arc">
        <svg viewBox="0 0 110 64" role="img" aria-label={`${g.label}: ${g.centre}`}>
          <path d="M 8 58 A 47 47 0 0 1 102 58" stroke="var(--bg-2)" strokeWidth="8" fill="none" />
          {showArc ? (
            /*
             * ── 🚨 `pathLength`, AND IT IS A BUG FIX, NOT A RESTYLE ──
             *
             * The old client hardcodes `stroke-dasharray: 110` with the comment
             * "110 = approximate arc length". It is not: the path is a semicircle
             * of radius 47, so it is π × 47 ≈ 147.7 long. Two consequences, both
             * visible on Josh's card:
             *
             *   at 100% the arc stops three-quarters of the way round, so a met
             *          target never looks met
             *   at   0% the offset lands the dash pattern in its OFF phase for the
             *          first 110 units and its ON phase for the last 38 — a red
             *          sliver at the far end of a FAILED gauge, which reads as a
             *          little buffer left. The exact opposite of what it means.
             *
             * `pathLength="100"` tells the renderer to treat the path as 100 units
             * long whatever its geometry, so the dash maths is the percentage and
             * both ends are exact. Nothing about the size, shape or colour moves.
             */
            <path
              d="M 8 58 A 47 47 0 0 1 102 58"
              pathLength={100}
              stroke={stroke}
              strokeWidth="8"
              fill="none"
              strokeDasharray={100}
              strokeDashoffset={(100 - g.pct).toFixed(1)}
            />
          ) : null}
          {g.state === 'failed' && !g.unset ? (
            <text x="55" y="50" textAnchor="middle" fontSize="18" fill="var(--text-3)">✕</text>
          ) : null}
        </svg>
        <div className="gauge-pct">{g.centre}</div>
      </div>
      <div className="gauge-sub">{g.sub}</div>
      {g.unset ? <div className="gauge-meta">{g.hint}</div>
        : g.meta ? <div className="gauge-meta">{g.meta}</div> : null}
    </div>
  );
}

/**
 * The consistency rule, in words, with the arithmetic done.
 *
 * ⚠ ALWAYS RENDERS, in one of three states. Reserving the slot is the same rule
 * the four gauges follow: a card that drops the row stops lining up with its
 * neighbour, so one breaching account knocks every element below it out of
 * alignment across the grid.
 */
function ConsistencyCallout({ m }: { m: AccountCardModel }) {
  const c = m.consistency;
  if (!c) {
    return (
      <div className="consistency-callout none">
        <div className="head">Consistency rule — not set</div>
        No consistency rule is configured for this account.
      </div>
    );
  }
  // ⚠ NOT SCOREABLE IS ITS OWN STATE, and it is not a green tick. With net
  // profit at or below zero there is no total for the best day to be a share of,
  // so the engine reports 0% and "no violation" — both true, and printed
  // together they read as "Best day is $195 — 0% of net profit -$315", which is
  // a malformed figure under a green tick. It was on Josh's card.
  if (!c.scoreable) {
    return (
      <div className="consistency-callout none">
        <div className="head">Consistency rule — nothing to measure yet</div>
        Your net profit is {money(c.totalProfit, 0)}, so there is no total for a
        best day to be a share of. {c.bestDay > 0
          ? <>Your best day so far is <b>{moneyPlain(c.bestDay, 0)}</b>. </>
          : null}
        The {num(c.thresholdPct, 0)}% rule starts to bite once you are net green.
      </div>
    );
  }
  if (c.inCompliance) {
    return (
      <div className="consistency-callout ok">
        <div className="head">✓ Consistency rule — within the {num(c.thresholdPct, 0)}% threshold</div>
        Best day is <b>{moneyPlain(c.bestDay, 0)}</b> — <b>{num(c.bestDayPct, 0)}%</b> of
        net profit {moneyPlain(c.totalProfit, 0)}.
      </div>
    );
  }
  return (
    <div className="consistency-callout">
      <div className="head">⚠ Consistency rule — over the {num(c.thresholdPct, 0)}% threshold</div>
      Best day was <b>{moneyPlain(c.bestDay, 0)}</b> — <b>{num(c.bestDayPct, 0)}%</b> of
      net profit {moneyPlain(c.totalProfit, 0)}.
      {c.bestDayPct > 100 ? (
        <> That is more than your whole net profit: the rest was given back on other days.</>
      ) : null}
      {' '}
      {c.trimBy == null ? (
        <>To comply, earn <span className="num">{moneyPlain(c.growBy, 0)}</span> more
          in green days. At a {num(c.thresholdPct, 0)}% threshold there is no amount
          you can trim off the best day — shaving it lowers your net profit by the
          same amount, so the share never falls.</>
      ) : (
        <>To comply, either earn <span className="num">{moneyPlain(c.growBy, 0)}</span> more
          in green days, or trim <span className="num">{moneyPlain(c.trimBy, 0)}</span> off
          the best day.</>
      )}
    </div>
  );
}

function TodayRoom({ m }: { m: AccountCardModel }) {
  const configured = m.dailyLimit > 0;
  const tone = configured ? barTone(m.todayPct) : 'zero';
  return (
    <div className="bar-row">
      <span className="label">Today’s Loss Room</span>
      <span className="value">
        {configured ? (
          <>
            {moneyPlain(m.todayRoom ?? 0, 0)}
            <span className="of"> / {moneyPlain(m.dailyLimit, 0)}</span>
            <span className="pct">{num(m.todayPct, 0)}%{m.locked ? ' · LIMIT HIT' : ''}</span>
          </>
        ) : <span className="of">Not configured</span>}
      </span>
      <div className="bar-track">
        <div
          className={`bar-fill${tone ? ` ${tone}` : ''}`}
          style={{ width: `${configured ? m.todayPct.toFixed(1) : 0}%` }}
        />
      </div>
    </div>
  );
}

/**
 * ⚠ THE FILL IS MEASURED AGAINST THE THRESHOLD, THE FIGURE IS THE RAW SHARE.
 * A full bar therefore means "exactly at the limit", and the cap rides the LABEL
 * so the reading is comparable at a glance: "Consistency (max 50%) … 378%". A
 * share on its own — especially one over 100% — cannot be read without the limit
 * it is being judged against.
 */
function ConsistencyBar({ m }: { m: AccountCardModel }) {
  const c = m.consistency;
  if (!c || !c.scoreable) {
    return (
      <div className="bar-row">
        <span className="label">Consistency</span>
        {/* ⚠ An em-dash, not "0%". A share of a non-positive total is not zero —
            it does not exist, and printing 0% claims a perfect result. */}
        <span className="value muted">{c ? '—' : 'Not configured'}</span>
        <div className="bar-track"><div className="bar-fill zero" style={{ width: '0%' }} /></div>
      </div>
    );
  }
  const pct = Math.max(0, Math.min(100, (c.bestDayPct / (c.thresholdPct || 100)) * 100));
  return (
    <div className="bar-row">
      <span className="label">
        Consistency <span className="muted">(max {num(c.thresholdPct, 0)}%)</span>
      </span>
      <span className="value">{num(c.bestDayPct, 0)}%</span>
      <div className="bar-track">
        <div
          className={`bar-fill${c.inCompliance ? '' : ' red'}`}
          style={{ width: `${pct.toFixed(1)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Risk of ruin: the share of resampled futures that touch the floor inside a
 * month.
 *
 * ⚠ IT REFUSES RATHER THAN INVENTING A LIMIT, and says which field is missing.
 * The old client substitutes a $2,500 drawdown and a $5,000 target when the
 * account has neither, which produces a confident percentage about rules the
 * firm never set. Neither of Josh's live accounts has a max drawdown, so this is
 * the state he will actually see — and "set this field" is useful where a
 * fabricated 6.4% is worse than nothing.
 *
 * ⚠ THE LINK APPEARS ONLY WHEN THERE IS A FIGURE. When ruin could not be
 * modelled the strip says which field is missing, and offering "Open simulator"
 * beside that would lead to a modal repeating the same sentence.
 */
function RuinStrip({ m, onOpen }: { m: AccountCardModel; onOpen: () => void }) {
  const inner = (
    <>
      <span className="ar-l">Risk of Ruin</span>
      {/* ⚠ ONE COLOUR, whatever the figure. The banner's Worst RoR is coloured by
          band because it RANKS accounts against each other; this one informs
          about a single account, and the old client keeps it flat for that
          reason. Two treatments of one fact on one screen is D102. */}
      <span className="ar-v">
        {m.ruin ? `${num(m.ruin.blowPct, 1)}%` : '—'}
      </span>
      <span className="ar-m">
        {m.ruin
          ? `resampled from your ${num(m.ruin.days, 0)} realised trading days${m.ruin.preliminary ? ' — preliminary, the pool is still thin' : ''}`
          : m.ruinBlocked}
      </span>
      {m.ruin ? <span className="ar-go">Open simulator →</span> : null}
    </>
  );

  if (!m.ruin) return <div className="acct-ror">{inner}</div>;
  return (
    <button
      type="button"
      className="acct-ror"
      onClick={onOpen}
      title="Open the Risk of Ruin simulator"
    >
      {inner}
    </button>
  );
}

// ---------------------------------------------------------------------------
// What this screen cannot do yet
// ---------------------------------------------------------------------------

/**
 * ⚠ NAMED RATHER THAN LEFT AS DEAD BUTTONS. Someone arriving from the current
 * client will look for these, and a disabled row of them reads as broken while
 * their absence reads as a missing feature. Saying it once, plainly, is the
 * convention every partial tab in this client follows.
 */
function NotWiredHere() {
  return (
    <div className="card">
      <h3>What this screen doesn’t do yet</h3>
      <p className="muted">
        {/* 🚨 THIS CARD IS A CLAIM ABOUT THE SCREEN, AND IT HAS NOW ROTTED FOUR
            TIMES. It said adding an account lived in the current client for a
            week after the modal shipped; it named archive and delete as missing
            after both landed in the menu six inches above it; and it went on
            naming "Coach Me — Compare Accounts" and the firm's commission table
            as unported while the button renders fifty lines above this card and
            the table renders inside the Edit Account form.

            ▶ The instruction that used to sit here — re-read it whenever
            anything lands — is gone. It failed four times, and then a FIFTH:
            this paragraph went on saying account merging lived in the old client
            while "Review & Merge" rendered ~570 lines above, wired to a modal
            that really writes.

            🔴 THAT FIFTH ONE IS WHY THE GUARD NOW READS BUTTONS.
            `notYetCards.test.ts` fails the build when a card names a section
            this file renders, a tab `App.tsx` routes, OR a button it draws. The
            note that used to sit here said the guard "cannot see a claim about a
            BUTTON… so this paragraph still needs a human eye" — correct at the
            time, and the human eye promptly missed the next one. It is a check
            now.

            ⚠ It reads the ABSENCE VOCABULARY too, and that is the part to watch:
            the fifth rot slipped through because "lives in the current client"
            was not a phrase the guard recognised as a claim of absence, so no
            check ever ran on the sentence. Inventing a new way to say "not here"
            makes this card invisible again — add the phrasing to `ABSENCE`. */}
        <b>Adding</b>, <b>editing</b>, <b>archiving</b>, <b>deleting</b>,{' '}
        <b>saving an account’s rules as a template</b>, the firm’s{' '}
        <b>commission table</b>, <b>Coach Me — Compare Accounts</b>,{' '}
        <b>merging two copies of one account</b> and{' '}
        <b>removing duplicate trades</b> all work here.
      </p>
      <p className="muted">
        {/* ⚠ "Apply commissions to existing trades" is NOT a gap and listing it
            as one was misleading in the other direction — this client derives
            the fee on read, so a saved rate is already in every figure and
            there is nothing to rewrite. The Edit Account form says exactly that
            on screen. */}
        Two parts of that form which reach beyond one account are still in the
        current client: the <b>adjustments</b> list — payouts, eval fees, resets
        — and <b>Recompute MAE/MFE</b>, which uses paid market data and
        overwrites excursion figures on hundreds of trades.
      </p>
      <p className="muted">
        {/* ⚠ This paragraph used to say the simulator was missing. It landed;
            the sentence did not move for a day. See the note above — this card
            is the only text on the tab that gets LESS true as the work goes
            well, and it has now rotted three separate times. */}
        The Risk of Ruin figure on each card opens the full simulator — click it.
      </p>
    </div>
  );
}

/**
 * The empty state's "+ New Account".
 *
 * ⚠ IT OWNS ITS OWN STATE rather than hoisting `creating` up to `AccountsTab`.
 * The empty branch returns before the board renders, so the two never coexist —
 * lifting the state would put a modal flag on a component that cannot show the
 * board it belongs to, for no gain.
 */
/**
 * ⚠ `autoOpen` SEEDS THE MODAL OPEN — it is not a controlled prop, and that is
 * deliberate. The reader must be able to close the modal and stay on this tab;
 * a controlled `open` would spring it back the moment anything re-rendered.
 * `useState`'s initial value runs once, which is exactly the semantics wanted:
 * "open this because I was asked to, then behave normally".
 */
function NewAccountButton({ autoOpen = false }: { autoOpen?: boolean } = {}) {
  const { reload } = useAccounts();
  const [open, setOpen] = useState(autoOpen);
  return (
    <>
      <button type="button" className="btn primary" onClick={() => setOpen(true)}>
        + New Account
      </button>
      {open ? (
        <AccountSetupModal
          onCancel={() => setOpen(false)}
          onCreated={() => { setOpen(false); reload(); }}
        />
      ) : null}
    </>
  );
}

/**
 * The card's ⋯ menu.
 *
 * All five of beta's items: Edit account · Archive/Unarchive · Save rules as
 * template (conditional) · a separator · Delete account in red.
 *
 * ⚠ THE SEPARATOR IS A SAFETY FIX, NOT DECORATION, and it is worth knowing
 * before anyone adds Delete. Beta's source says the delete "was previously an
 * unlabelled ✕ adjacent to archive — one misclick from the only irreversible
 * action on the card". Archive and Delete must never sit adjacent and unlabelled
 * again.
 *
 * ⚠ ARCHIVE HAS NO CONFIRMATION, and that is right: it is reversible from the
 * same menu, in one click, and a confirm on a reversible action trains people to
 * dismiss confirms on the irreversible one.
 */
function CardMenu(
  { accountId, archived, onEdit, onArchived, onDelete, canSaveTemplate, onSaveTemplate }: {
    accountId: string;
    archived: boolean;
    onEdit: () => void;
    onArchived: () => void;
    onDelete: () => void;
    canSaveTemplate: boolean;
    onSaveTemplate: () => void;
  },
) {
  const { sb } = useSupabase();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ⚠ Closes on any outside click, as beta's does. Without it two open menus can
  // overlap and the one underneath is unreachable.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  async function toggle() {
    if (!sb) return;
    setBusy(true);
    setError(null);
    // ⚠ The TARGET state, not a flip — see `setAccountArchived`. Two tabs on a
    // stale value must not be able to land on opposite answers.
    const res = await setAccountArchived(sb, accountId, !archived);
    setBusy(false);
    if (!res.ok) { setError(res.error ?? 'That did not save.'); return; }
    setOpen(false);
    onArchived();
  }

  return (
    <div className="acct-menu-wrap" onClick={(e) => e.stopPropagation()}>
      <button
        type="button" className="btn btn-icon acct-menu-btn"
        aria-label="More actions" aria-expanded={open} title="More actions"
        onClick={() => setOpen((v) => !v)}
      >⋯</button>

      {open ? (
        <div className="acct-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => { setOpen(false); onEdit(); }}>
            <span className="am-gl" aria-hidden="true">✎</span>Edit account
          </button>
          <button type="button" role="menuitem" disabled={busy} onClick={() => void toggle()}>
            <span className="am-gl" aria-hidden="true">{archived ? '↩' : '🗄'}</span>
            {busy ? 'Saving…' : archived ? 'Unarchive' : 'Archive'}
          </button>

          {/* ⚠ ABOVE the separator, because it is not destructive and not even
              a change to this account — it copies its rules out. */}
          {canSaveTemplate ? (
            <button type="button" role="menuitem"
              onClick={() => { setOpen(false); onSaveTemplate(); }}>
              <span className="am-gl" aria-hidden="true">⎘</span>Save rules as template
            </button>
          ) : null}

          {/* 🚨 THE SEPARATOR IS THE SAFETY, NOT DECORATION. Beta's source:
              the delete "was previously an unlabelled ✕ adjacent to archive —
              one misclick from the only irreversible action on the card". These
              two must never sit adjacent and unlabelled again. */}
          <div className="am-sep" role="separator" />
          <button type="button" role="menuitem" className="danger"
            onClick={() => { setOpen(false); onDelete(); }}>
            <span className="am-gl" aria-hidden="true">✕</span>Delete account
          </button>
        </div>
      ) : null}

      {/* ⚠ OUTSIDE the menu, so it survives the menu closing. A failure message
          that disappears with the thing that caused it is not a message. */}
      {error ? <span className="acct-menu-err red">{error}</span> : null}
    </div>
  );
}
